import React, { useState } from "react";
import {
  Box,
  Button,
  TextField,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  List,
  ListItem,
  ListItemSecondaryAction,
  ListItemText,
  IconButton,
  CircularProgress,
  Stack,
  Divider,
  Alert
} from "@mui/material";
import { Delete as DeleteIcon, Add as AddIcon } from "@mui/icons-material";
import { PredifineItem } from "utils/types/Info.types";


interface ListManagerDialogProps {
  open: boolean;
  onClose: () => void;
  subCategory: string;
  items: PredifineItem[];
  loading: boolean;
  isSaving: boolean;
  onSave: (itemName: string) => void;
  onDelete: (id: string) => void;
}

export const ListManagerDialog: React.FC<ListManagerDialogProps> = ({
  open,
  onClose,
  subCategory,
  items,
  loading,
  isSaving,
  onSave,
  onDelete,
}) => {
  const [newItem, setNewItem] = useState("");

  const handleSaveClick = () => {
    onSave(newItem);
    setNewItem(""); // Clear input after save
  };

  // Capitalize the first letter for a user-friendly title
  const title = `Gérer ${subCategory.charAt(0).toUpperCase() + subCategory.slice(1)}s`;

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          {/* Input Form */}
          <Stack direction="row" spacing={1} alignItems="center">
            <TextField
              label={`Nouveau ${subCategory}`}
              value={newItem}
              onChange={(e) => setNewItem(e.target.value)}
              fullWidth
              margin="none"
              disabled={isSaving}
              onKeyDown={(e) => e.key === 'Enter' && handleSaveClick()}
            />
            <Button
              onClick={handleSaveClick}
              color="primary"
              variant="contained"
              disabled={isSaving || !newItem.trim()}
              startIcon={isSaving ? <CircularProgress size={20} color="inherit" /> : <AddIcon />}
            >
              Ajouter
            </Button>
          </Stack>
          
          <Divider sx={{ my: 2 }} />

          {/* List Content */}
          {loading ? (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
              <CircularProgress />
            </Box>
          ) : (
            <Box sx={{ maxHeight: '400px', overflowY: 'auto' }}>
              {items.length === 0 ? (
                <Alert severity="info">Aucun élément dans cette liste.</Alert>
              ) : (
                <List dense>
                  {items.map((item) => (
                    <ListItem key={item.id} divider>
                      <ListItemText primary={item.name} />
                      <ListItemSecondaryAction>
                        <IconButton 
                          edge="end" 
                          aria-label="delete"
                          onClick={() => onDelete(item.id!)}
                          disabled={isSaving}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </ListItemSecondaryAction>
                    </ListItem>
                  ))}
                </List>
              )}
            </Box>
          )}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onClose} variant="outlined">Fermer</Button>
      </DialogActions>
    </Dialog>
  );
};